import { DisplayObject, InteractionEvent } from 'pixi.js';
import { Focusable } from './FocusManager';
import { GameTime, IDisposable } from '../utils';
import { Settings } from '../settings';


export interface InteractiveWrapperOptions
{
    onHover: (ev?:InteractionEvent) => any;
    onFocus?: () => any;
    onTap: (ev?:InteractionEvent) => any;
    onActivate?: () => any;
    forceDwell?: boolean;
}

const HOVER_DELAY = 500;
const DWELL_TIME = 2000;
const DWELL_IMMEDIATELY = true;

/**
 * InteractiveWrapper wraps a focusable display object to handle hover & click events for you,
 * running them through some timers to delay hovers as needed as well as handle dwell timed clicks.
 */
export class InteractiveWrapper implements IDisposable
{
    private target: DisplayObject & Focusable;
    private onHover: () => any;
    private onFocus: () => any;
    private onTap: (ev: InteractionEvent) => any;
    private onActivate: () => any;
    private hoverTimer: number;
    private dwellTimer: number;
    private forceDwell: boolean;

    constructor(target: DisplayObject & Focusable, opts: InteractiveWrapperOptions)
    {
        this.target = target;
        this.forceDwell = !!opts.forceDwell;
        this.onHover = opts.onHover;
        this.onFocus = opts.onFocus || opts.onHover;
        this.onTap = opts.onTap;
        this.onActivate = opts.onActivate || opts.onTap;
        this.hoverTimer = 0;
        this.dwellTimer = 0;
        this.onHoverTick = this.onHoverTick.bind(this);
        this.onDwellTick = this.onDwellTick.bind(this);

        target.on('pointerover', this.onOver, this);
        target.on('pointerout', this.onOut, this);
        target.on('pointertap', this.onItemTap, this);
    }

    private get dwell() { return Settings.instance.enableDwellClicks.value; }
    private get showRestArea() { return Settings.instance.showRestArea.value; }
    private get gridGameScene() { return Settings.instance.gridGameScene.value; }


    private async onOver(ev: InteractionEvent): Promise<void>
    {
        this.dwellTimer = 0;

        let currSceneType = this.target.parent.constructor.name; // immediately enable dwell clicks on non-game scenes 
        if ((this.dwell || (!this.dwell && this.showRestArea && !this.gridGameScene))
            && DWELL_IMMEDIATELY && !this.forceDwell)
        {
            this.target.focusVisible = true;
            GameTime.gameTick.subscribe(this.onDwellTick);
        }
        if (ev)
        {
            this.hoverTimer = 0;
            GameTime.gameTick.subscribe(this.onHoverTick);
        }
        else
        {
            await this.onFocus();

            // bail if the target no longer has focus
            if (!this.target.focus)
            {
                return;
            }
            if (this.forceDwell || (this.dwell && !DWELL_IMMEDIATELY))
            {
                this.target.focusVisible = true;
                GameTime.gameTick.subscribe(this.onDwellTick);
            }
        }
    }

    private async onHoverTick(elapsed: number): Promise<void>
    {
        this.hoverTimer += elapsed;

        if (this.hoverTimer >= HOVER_DELAY)
        {
            GameTime.gameTick.unsubscribe(this.onHoverTick);
            await this.onHover();

            // because we set the hover timer to 0 on pointerout, if it is 0 after hitting
            // the HOVER_DELAY then we moused out and need to bail
            if (this.hoverTimer === 0)
            {
                return;
            }
            if (this.forceDwell || (this.dwell && !DWELL_IMMEDIATELY))
            {
                this.target.focusVisible = true;
                GameTime.gameTick.subscribe(this.onDwellTick);
            }
        }
    }

    private onDwellTick(elapsed: number): void
    {
        this.dwellTimer += elapsed;

        if (this.dwellTimer >= DWELL_TIME)
        {
            this.clearItem();
            this.target.activate();
        }
    }

    private onOut(_ev: InteractionEvent): void
    {
        this.clearItem();
    }

    private onItemTap(ev: InteractionEvent): void
    {
        this.clearItem();
        if (ev)
        {
            this.onTap(ev);
        }
        else
        {
            this.onActivate();
        }
    }

    private clearItem()
    {
        this.dwellTimer = this.hoverTimer = 0;
        this.target.focusVisible = false;
        GameTime.gameTick.unsubscribe(this.onHoverTick);
        GameTime.gameTick.unsubscribe(this.onDwellTick);
    }

    public dispose()
    {
        this.target.off('pointerover', this.onOver, this);
        this.target.off('pointerout', this.onOut, this);
        this.target.off('pointertap', this.onItemTap, this);
        GameTime.gameTick.unsubscribe(this.onHoverTick);
        GameTime.gameTick.unsubscribe(this.onDwellTick);
    }

    public destroy()
    {
        this.dispose();
    }
}